import { PayExemptionTypes } from '@/Domain/enum';

/**
 * CON-451: los descuentos dejaron de identificarse por el bit-flag y pasaron a Guid.
 *
 * El catálogo (`masterdata/processConfigurationPayExemption`) sigue exponiendo el bit en `type`,
 * pero SOLO para los descuentos generales; los personalizados de cliente lo traen a null. Por eso
 * el bit ya no sirve como clave —hay que cruzar por `id`— y este catálogo es la única vía para
 * resolver las reglas que sí dependen del tipo concreto: discapacidad, familia numerosa y
 * "sin motivo".
 */
export default class PayExemptionCatalog {
    public static sameId(left: any, right: any): boolean {
        const normalizedLeft = PayExemptionCatalog.normalize(left);
        return normalizedLeft !== null && normalizedLeft === PayExemptionCatalog.normalize(right);
    }

    /** Los Guid viajan como string; se normalizan para que el cruce no dependa del casing. */
    private static normalize(id: any): string | null {
        return id === null || id === undefined ? null : String(id).toLowerCase();
    }

    private _byId: Map<string, any>;
    private _idByType: Map<number, string>;

    constructor(processConfigurationPayExemptions?: any[]) {
        this._byId = new Map();
        this._idByType = new Map();

        (processConfigurationPayExemptions || []).forEach(payExemption => {
            const id = PayExemptionCatalog.normalize(payExemption.id);
            if (id === null) {
                return;
            }
            this._byId.set(id, payExemption);
            if (payExemption.type !== null && payExemption.type !== undefined) {
                this._idByType.set(Number(payExemption.type), id);
            }
        });
    }

    public getById(id: any) {
        const normalizedId = PayExemptionCatalog.normalize(id);
        return normalizedId !== null && this._byId.has(normalizedId) ? this._byId.get(normalizedId) : null;
    }

    /** Guid del descuento general con ese bit, o null si el catálogo no lo trae. */
    public getIdByType(type: number): string | null {
        const id = this._idByType.get(type);
        return id !== undefined ? id : null;
    }

    /** Bit del descuento, o null si es personalizado de cliente (o no está en el catálogo). */
    public getTypeById(id: any): number | null {
        const payExemption = this.getById(id);
        return payExemption && payExemption.type !== null && payExemption.type !== undefined
            ? Number(payExemption.type)
            : null;
    }

    public isType(id: any, type: number): boolean {
        return this.getTypeById(id) === type;
    }

    public get noneId(): string | null {
        return this.getIdByType(PayExemptionTypes.enum.NONE);
    }

    public get noReasonId(): string | null {
        return this.getIdByType(PayExemptionTypes.enum.NOREASON);
    }

    public get disabilityId(): string | null {
        return this.getIdByType(PayExemptionTypes.enum.DISABILITY);
    }

    /** "Sin exención": el proceso no oferta descuentos (None) o no se pide ninguno (NoReason). */
    public isNoneOrNoReason(id: any): boolean {
        const type = this.getTypeById(id);
        return type === PayExemptionTypes.enum.NONE || type === PayExemptionTypes.enum.NOREASON;
    }

    public isNoReason(id: any): boolean {
        return this.isType(id, PayExemptionTypes.enum.NOREASON);
    }

    /** Familia numerosa (general o especial): el caso que obliga a informar la comunidad autónoma. */
    public isLargeFamily(id: any): boolean {
        const type = this.getTypeById(id);
        return type === PayExemptionTypes.enum.GENERALLARGEFAMILY || type === PayExemptionTypes.enum.SPECIALLARGEFAMILY;
    }

    public isDisability(id: any): boolean {
        return this.isType(id, PayExemptionTypes.enum.DISABILITY);
    }
}
